import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Separator, useFeedback } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { trpc } from '@/lib/trpc'
import { ArrowRight, ArrowLeft, CheckCircle, Paperclip, X } from 'lucide-react'

type RequestType = 'content' | 'design' | 'bug' | 'new-page' | 'other'
type Priority = 'low' | 'normal' | 'urgent'
type Step = 'email' | 'org' | 'type' | 'page' | 'details' | 'submit'

interface Organization {
  id: string
  name: string
  clientName: string
  websiteUrl: string
  cmsOrgId?: string
}

interface CmsPage {
  id: string
  title: string
  slug: string
}

const REQUEST_TYPES: { value: RequestType; label: string; description: string }[] = [
  { value: 'content', label: 'Content Update', description: 'Text, images, events, or other content changes' },
  { value: 'design', label: 'Design Change', description: 'Colors, layout, fonts, or visual adjustments' },
  { value: 'bug', label: 'Bug Fix', description: 'Something is broken or not working correctly' },
  { value: 'new-page', label: 'New Page', description: 'Add a new page or section to your site' },
  { value: 'other', label: 'Other', description: "Something that doesn't fit the categories above" },
]

const PRIORITIES: { value: Priority; label: string; description: string }[] = [
  { value: 'low', label: 'Nice to Have', description: 'No rush' },
  { value: 'normal', label: 'Normal', description: 'Within a few days' },
  { value: 'urgent', label: 'Urgent', description: 'As soon as possible' },
]

const TYPE_NEEDS_PAGE: RequestType[] = ['content', 'design', 'bug']

const inputClass =
  'w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary'

function getSteps(isMultiOrg: boolean, type: RequestType | null): Step[] {
  const steps: Step[] = ['email']
  if (isMultiOrg) steps.push('org')
  steps.push('type')
  if (type === null || TYPE_NEEDS_PAGE.includes(type)) steps.push('page')
  steps.push('details')
  steps.push('submit')
  return steps
}

export default function RequestsPage() {
  const breadcrumbs = generateBreadcrumbs('/requests', breadcrumbLabels)
  useSEO({
    title: 'Request an Update',
    description:
      'Submit a website update request to Fugue Lab. Existing clients can request content changes, design updates, bug fixes, and more.',
    url: '/requests',
    canonical: '/requests',
    breadcrumbs,
  })

  const { toast } = useFeedback()
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [type, setType] = useState<RequestType | null>(null)
  const [cmsPages, setCmsPages] = useState<CmsPage[]>([])
  const [cmsLoading, setCmsLoading] = useState(false)
  const [selectedPage, setSelectedPage] = useState<CmsPage | null>(null)
  const [pageUrl, setPageUrl] = useState('')
  const [description, setDescription] = useState('')
  const [visualDescription, setVisualDescription] = useState('')
  const [attachments, setAttachments] = useState<{ key: string; filename: string; contentType: string }[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [priority, setPriority] = useState<Priority>('normal')
  const [honeypot, setHoneypot] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [issueNumber, setIssueNumber] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)

  const isMultiOrg = organizations.length > 1
  // Before type is committed (on the type step itself), show max-path dots so page dot doesn't flicker
  const steps = getSteps(isMultiOrg, step === 'type' ? null : type)
  const currentIdx = steps.indexOf(step)

  const goBack = () => {
    setError('')
    setStep(steps[currentIdx - 1])
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsVerifying(true)
    try {
      const result = await trpc.requests.verify.mutate({ email })
      if (!result.valid || result.organizations.length === 0) {
        setError(
          "We couldn't find that email in our records. Try the address you signed up with, or contact us to get started."
        )
        return
      }
      const orgs = result.organizations as Organization[]
      setOrganizations(orgs)
      if (orgs.length === 1) {
        setSelectedOrg(orgs[0])
        setStep('type')
      } else {
        setStep('org')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setIsVerifying(false)
    }
  }

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org)
    setStep('type')
  }

  const handleSelectType = async (t: RequestType) => {
    setType(t)
    setSelectedPage(null)
    setPageUrl('')
    setCmsPages([])
    if (TYPE_NEEDS_PAGE.includes(t)) {
      setStep('page')
      if (selectedOrg?.cmsOrgId) {
        setCmsLoading(true)
        try {
          const result = await trpc.requests.getPages.mutate({
            email,
            organizationId: selectedOrg.id,
          })
          setCmsPages(result.pages as CmsPage[])
        } catch {
          setCmsPages([])
        } finally {
          setCmsLoading(false)
        }
      }
    } else {
      setStep('details')
    }
  }

  const handlePageNext = () => {
    if (type === 'bug' && !selectedPage && !pageUrl) {
      setError('Please select or enter the affected page.')
      return
    }
    setError('')
    setStep('details')
  }

  const handleDetailsNext = () => {
    if (description.trim().length < 10) {
      setError('Please describe what you need (at least 10 characters).')
      return
    }
    setError('')
    setStep('submit')
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    if (attachments.length + files.length > 10) {
      toast('Maximum 10 files allowed.', 'error')
      return
    }
    setIsUploading(true)
    try {
      for (const file of Array.from(files)) {
        if (file.size > 20 * 1024 * 1024) {
          toast(`${file.name} exceeds 20MB limit.`, 'error')
          continue
        }
        const { uploadUrl, key } = await trpc.requests.getUploadUrl.mutate({
          email,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
        })
        await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        })
        setAttachments((prev) => [
          ...prev,
          { key, filename: file.name, contentType: file.type || 'application/octet-stream' },
        ])
      }
    } catch {
      toast('Failed to upload file. Please try again.', 'error')
    } finally {
      setIsUploading(false)
      e.target.value = ''
    }
  }

  const removeAttachment = (key: string) => {
    setAttachments((prev) => prev.filter((a) => a.key !== key))
  }

  const handleSubmit = async () => {
    if (honeypot || !selectedOrg || !type) return
    setIsSubmitting(true)
    setError('')
    try {
      let effectivePageUrl: string | undefined
      if (selectedPage) {
        if (selectedOrg.websiteUrl) {
          const slug = selectedPage.slug.startsWith('/') ? selectedPage.slug : `/${selectedPage.slug}`
          effectivePageUrl = `${selectedOrg.websiteUrl.replace(/\/$/, '')}${slug}`
        }
      } else if (pageUrl) {
        effectivePageUrl = pageUrl
      }

      const result = await trpc.requests.create.mutate({
        email,
        organizationId: selectedOrg.id,
        type,
        description,
        pageUrl: effectivePageUrl,
        priority,
        visualDescription: visualDescription || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      })
      if (result.success) {
        setIssueNumber(result.issueNumber)
        setSubmitted(true)
      }
    } catch {
      setError('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const typeLabel = REQUEST_TYPES.find((t) => t.value === type)?.label ?? ''
  const pageLabel = selectedPage ? selectedPage.title : pageUrl

  if (submitted) {
    return (
      <div className="bg-background min-h-screen">
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-600" />
          <h2 className="mb-2 text-2xl font-semibold">Got it. We're on it.</h2>
          <p className="text-muted-foreground mb-2">
            Request #{issueNumber} submitted for {selectedOrg?.name}.
          </p>
          <p className="text-muted-foreground mb-8">You'll receive a confirmation email shortly.</p>
          <Button asChild variant="outline">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="mx-auto max-w-2xl px-4 py-24">
        {/* Header */}
        <section className="mb-10 text-center">
          <h1 className="mb-4 text-4xl font-bold tracking-tight">Request an Update</h1>
          <Separator className="bg-border mx-auto mb-4 h-[2px] w-16" />
          <p className="text-muted-foreground text-lg">Tell us what you need. We'll handle it.</p>
        </section>

        {/* Progress dots */}
        {step !== 'email' && (
          <div className="mb-10 flex items-center justify-center gap-2">
            {steps.map((s, i) => (
              <div
                key={s}
                className={`rounded-full transition-all duration-200 ${
                  i < currentIdx
                    ? 'bg-primary h-2 w-2'
                    : i === currentIdx
                      ? 'bg-primary ring-primary/30 h-3 w-3 ring-2'
                      : 'bg-border h-2 w-2'
                }`}
              />
            ))}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="bg-destructive/10 text-destructive mb-6 rounded-lg p-4 text-sm">
            {error}
            {step === 'email' && (
              <span>
                {' '}
                <Link to="/contact" className="hover:text-foreground underline">
                  Contact us
                </Link>{' '}
                to get started.
              </span>
            )}
          </div>
        )}

        {/* Step: Email */}
        {step === 'email' && (
          <form onSubmit={handleVerify} className="space-y-6">
            <p className="text-muted-foreground">Enter the email address associated with your Fugue Lab account.</p>
            <div>
              <label htmlFor="verify-email" className="mb-2 block text-sm font-medium">
                Email
              </label>
              <input
                id="verify-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isVerifying}
                className={inputClass}
                placeholder="you@yourorg.com"
              />
            </div>
            <Button type="submit" variant="default" className="w-full py-3" disabled={isVerifying}>
              {isVerifying ? 'Verifying...' : 'Continue'}
              {!isVerifying && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>
        )}

        {/* Step: Org Picker */}
        {step === 'org' && (
          <div className="space-y-6">
            <p className="font-medium">Which site is this request for?</p>
            <div className="space-y-3">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => handleSelectOrg(org)}
                  className="border-border hover:border-primary hover:bg-primary/5 w-full rounded-lg border p-4 text-left transition-colors"
                >
                  <p className="font-medium">{org.name}</p>
                  {org.websiteUrl && <p className="text-muted-foreground mt-1 text-sm">{org.websiteUrl}</p>}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={goBack}
              className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Use a different email
            </button>
          </div>
        )}

        {/* Step: Request Type */}
        {step === 'type' && (
          <div className="space-y-6">
            {selectedOrg && (
              <div className="bg-muted flex items-center justify-between rounded-lg px-4 py-3 text-sm">
                <span>
                  For <span className="font-medium">{selectedOrg.name}</span>
                </span>
                {isMultiOrg && (
                  <button
                    type="button"
                    onClick={goBack}
                    className="text-muted-foreground hover:text-foreground text-xs underline"
                  >
                    change
                  </button>
                )}
              </div>
            )}
            <p className="font-medium">What kind of update do you need?</p>
            <div className="space-y-3">
              {REQUEST_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => handleSelectType(t.value)}
                  className="border-border hover:border-primary hover:bg-primary/5 w-full rounded-lg border p-4 text-left transition-colors"
                >
                  <p className="font-medium">{t.label}</p>
                  <p className="text-muted-foreground mt-0.5 text-sm">{t.description}</p>
                </button>
              ))}
            </div>
            {!isMultiOrg && (
              <button
                type="button"
                onClick={goBack}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
          </div>
        )}

        {/* Step: Page */}
        {step === 'page' && (
          <div className="space-y-6">
            <p className="font-medium">
              Which page is this about?
              {type !== 'bug' && <span className="text-muted-foreground text-sm font-normal"> (optional)</span>}
            </p>

            {cmsLoading && <p className="text-muted-foreground text-sm">Loading your site pages...</p>}

            {!cmsLoading && cmsPages.length > 0 && (
              <div className="space-y-2">
                {cmsPages.map((page) => (
                  <button
                    key={page.id}
                    type="button"
                    onClick={() => {
                      setSelectedPage(page)
                      setPageUrl('')
                    }}
                    className={`w-full rounded-lg border p-3 text-left transition-colors ${
                      selectedPage?.id === page.id
                        ? 'border-primary bg-primary/5 ring-primary ring-2'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <p className="text-sm font-medium">{page.title}</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">/{page.slug}</p>
                  </button>
                ))}
              </div>
            )}

            {!cmsLoading && (
              <div>
                <label htmlFor="page-url" className="mb-2 block text-sm font-medium">
                  {cmsPages.length > 0 ? 'Or enter the URL directly' : 'Page URL'}
                  {type !== 'bug' && <span className="text-muted-foreground font-normal"> (optional)</span>}
                </label>
                <input
                  id="page-url"
                  type="url"
                  value={pageUrl}
                  onChange={(e) => {
                    setPageUrl(e.target.value)
                    setSelectedPage(null)
                  }}
                  className={inputClass}
                  placeholder={selectedOrg?.websiteUrl ? `${selectedOrg.websiteUrl}/page` : 'https://yoursite.com/page'}
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={goBack}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button type="button" variant="default" className="flex-1" onClick={handlePageNext}>
                {type !== 'bug' && !selectedPage && !pageUrl ? 'Skip' : 'Continue'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step: Details */}
        {step === 'details' && (
          <div className="space-y-6">
            <p className="font-medium">Describe what you need.</p>

            <div>
              <label htmlFor="description" className="mb-2 block text-sm font-medium">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                required
                minLength={10}
                maxLength={5000}
                className={`${inputClass} resize-none`}
                placeholder={
                  type === 'content'
                    ? 'What content needs to change? Include the new text or describe the images...'
                    : type === 'design'
                      ? 'Describe the visual changes: colors, layout, fonts, spacing...'
                      : type === 'bug'
                        ? "Describe what's broken and how to reproduce it..."
                        : type === 'new-page'
                          ? 'What should be on this page? Who is it for?'
                          : "Tell us what you'd like done..."
                }
              />
              <p className="text-muted-foreground mt-1 text-xs">{description.length}/5000</p>
            </div>

            {(type === 'design' || type === 'content') && (
              <div>
                <label htmlFor="visual-description" className="mb-2 block text-sm font-medium">
                  Visual details <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  id="visual-description"
                  value={visualDescription}
                  onChange={(e) => setVisualDescription(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  className={`${inputClass} resize-none`}
                  placeholder="Colors, fonts, reference images, examples..."
                />
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium">
                Attachments{' '}
                <span className="text-muted-foreground font-normal">(optional, max 10 files · 20MB each)</span>
              </label>
              <label
                className={`border-border hover:border-primary hover:bg-primary/5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-3 transition-colors ${
                  isUploading ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                <Paperclip className="text-muted-foreground h-4 w-4" />
                <span className="text-muted-foreground text-sm">
                  {isUploading ? 'Uploading...' : 'Click to attach files'}
                </span>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  disabled={isUploading}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt,.mp3,.mp4,.wav,.mov,.zip"
                />
              </label>
              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((a) => (
                    <div
                      key={a.key}
                      className="bg-muted flex items-center justify-between rounded-lg px-3 py-2 text-sm"
                    >
                      <span className="mr-2 truncate">{a.filename}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.key)}
                        className="text-muted-foreground hover:text-foreground shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={goBack}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button type="button" variant="default" className="flex-1" onClick={handleDetailsNext}>
                Review <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step: Submit — priority + summary */}
        {step === 'submit' && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-muted space-y-3 rounded-lg p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Site</span>
                <span className="font-medium">{selectedOrg?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className="font-medium">{typeLabel}</span>
              </div>
              {pageLabel && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground shrink-0">Page</span>
                  <span className="truncate text-right font-medium">{pageLabel}</span>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Request</span>
                <p className="text-foreground mt-1 leading-relaxed">
                  {description.length > 140 ? description.slice(0, 140) + '…' : description}
                </p>
              </div>
            </div>

            {/* Priority */}
            <div>
              <span id="request-priority-label" className="mb-3 block text-sm font-medium">
                Priority
              </span>
              <div role="group" aria-labelledby="request-priority-label" className="grid grid-cols-3 gap-3">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    className={`rounded-lg border p-3 text-center transition-colors ${
                      priority === p.value
                        ? 'border-primary bg-primary/5 ring-primary ring-2'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <p className="text-sm font-medium">{p.label}</p>
                    <p className="text-muted-foreground mt-1 text-xs">{p.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="honeypot"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
            />

            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={goBack} disabled={isSubmitting}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                type="button"
                variant="default"
                className="flex-1 py-3"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
